'use client';

import React, { useState, useMemo, useCallback } from 'react';
import { Stock } from '@/types/watchlist';
import {
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import StockActionIcons from '@/components/stocks/StockActionIcons';
import StockNotesModal from '@/components/stocks/StockNotesModal';

interface WatchlistStocksTableProps {
  stocks: Stock[];
  onRemoveStock?: (isin: string) => void;
  onOpenInNewWindow?: (isin: string) => void;
  loading?: boolean;
  title?: string;
  showRemoveAction?: boolean;
  emptyMessage?: string;
  watchlistId?: string;
}

type SortField = keyof Stock;
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

const WatchlistStocksTable: React.FC<WatchlistStocksTableProps> = ({
  stocks,
  onRemoveStock,
  onOpenInNewWindow,
  loading = false,
  title = "watchlist",
  showRemoveAction = true,
  emptyMessage,
  watchlistId,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<Stock | null>(null);

  const openNotesModal = useCallback((stock: Stock) => {
    setSelectedStock(stock);
    setIsNotesModalOpen(true);
  }, []);

  const closeNotesModal = useCallback(() => {
    setIsNotesModalOpen(false);
    setSelectedStock(null);
  }, []);

  const sortedStocks = useMemo(() => {
    if (!stocks || stocks.length === 0) return [];

    return [...stocks].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'asc' ? -1 : 1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      if (sortConfig.field === 'added_at' && typeof aValue === 'string' && typeof bValue === 'string') {
        const dateA = new Date(aValue).getTime();
        const dateB = new Date(bValue).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }

      return 0;
    });
  }, [stocks, sortConfig]);

  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // CSV Export function
  const downloadCSV = () => {
    const csvHeader = [
      'ISIN',
      'Stock Name',
      'NSE Code',
      'BSE Code',
      'Stock Code',
      'Industry',
      'Added At',
      'Current Price',
      'Price at Add',
      'Return Since Add (%)',
      '1D Return (%)',
      '1W Return (%)',
      '1M Return (%)',
      '3M Return (%)',
      '6M Return (%)',
      '1Y Return (%)',
      '3Y Return (%)',
      '5Y Return (%)',
      'Market Cap (Cr)',
      'TradingView Link',
      'Screener Link',
      'External Code'
    ].join(',');

    const csvRows = sortedStocks.map(stock => {
      const row = [
        stock.isin,
        `"${stock.name || ''}"`,
        stock.nse_code || '',
        stock.bse_code || '',
        stock.stock_code || '',
        `"${stock.industry || ''}"`,
        stock.added_at || '',
        stock.current_price ? stock.current_price.toFixed(2) : 'N/A',
        stock.price_at_add ? stock.price_at_add.toFixed(2) : 'N/A',
        stock.return_since_add != null ? stock.return_since_add.toFixed(2) : 'N/A',
        stock.return_over_1day != null ? stock.return_over_1day.toFixed(2) : 'N/A',
        stock.return_over_1week != null ? stock.return_over_1week.toFixed(2) : 'N/A',
        stock.return_over_1month != null ? stock.return_over_1month.toFixed(2) : 'N/A',
        stock.return_over_3months != null ? stock.return_over_3months.toFixed(2) : 'N/A',
        stock.return_over_6months != null ? stock.return_over_6months.toFixed(2) : 'N/A',
        stock.return_over_1year != null ? stock.return_over_1year.toFixed(2) : 'N/A',
        stock.return_over_3years != null ? stock.return_over_3years.toFixed(2) : 'N/A',
        stock.return_over_5years != null ? stock.return_over_5years.toFixed(2) : 'N/A',
        stock.market_cap ? stock.market_cap.toFixed(2) : 'N/A',
        stock.trading_view_navlink || '',
        stock.screener_navlink || '',
        stock.stock_code ? `"${stock.stock_code},"` : ''
      ].join(',');
      return row;
    });

    const csvContent = [csvHeader, ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `watchlist-stocks-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatPercentage = (value: number | undefined | null): React.ReactElement | string => {
    if (value == null || isNaN(value)) return '—';
    const isPositive = value >= 0;
    return (
      <span className={`inline-flex items-center gap-1 text-sm font-medium ${
        isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
      }`}>
        {isPositive ? <ArrowUpIcon className="h-3 w-3" /> : <ArrowDownIcon className="h-3 w-3" />}
        {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  const formatVolume = (value: number | undefined | null): string => {
    if (value == null || isNaN(value)) return '—';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 10000000) return `${sign}${(absValue / 10000000).toFixed(1)}Cr`;
    if (absValue >= 100000) return `${sign}${(absValue / 100000).toFixed(1)}L`;
    if (absValue >= 1000) return `${sign}${(absValue / 1000).toFixed(0)}K`;
    return `${sign}${absValue.toLocaleString()}`;
  };

  const formatMarketCap = (value: number | undefined | null): string => {
    if (value == null || isNaN(value)) return '—';
    const isNegative = value < 0;
    const absValue = Math.abs(value);
    const sign = isNegative ? '-' : '';
    
    if (absValue >= 1000) return `${sign}₹${(absValue / 1000).toFixed(1)}K Cr`;
    return `${sign}₹${absValue.toFixed(1)} Cr`;
  };

  const getVolumeHighlighting = (stock: Stock) => {
    const { volume_1day, volume_1week, volume_1month } = stock;
    
    if (!volume_1day || volume_1day === 0) return '';
    
    let isHighlighted = false;
    let isStrongHighlight = false;
    
    if (volume_1week && volume_1day > volume_1week * 1.5) {
      isHighlighted = true;
    }
    
    if (volume_1month && volume_1day > volume_1month * 1.5) {
      isHighlighted = true;
    }
    
    if (volume_1week && volume_1month && volume_1week > volume_1month * 2) {
      isStrongHighlight = true;
    }
    
    if (isStrongHighlight) {
      return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 font-semibold';
    } else if (isHighlighted) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 font-medium';
    }
    
    return '';
  };

  const formatAddedDate = (stock: Stock): string => {
    if (!stock.added_at) return '—';
    return new Date(stock.added_at).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const SortButton: React.FC<{ field: SortField; children: React.ReactNode; className?: string }> = ({ 
    field, 
    children, 
    className = "" 
  }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 text-xs font-semibold text-zinc-500 dark:text-zinc-400 hover:text-zinc-800 dark:hover:text-zinc-100 transition-colors whitespace-nowrap ${className}`}
    >
      {children}
      {sortConfig.field === field ? (
        sortConfig.direction === 'asc' ? (
          <ChevronUpIcon className="h-3 w-3" />
        ) : (
          <ChevronDownIcon className="h-3 w-3" />
        )
      ) : (
        <div className="h-3 w-3" />
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="w-full space-y-4">
        <div className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              Loading stocks...
            </div>
          </div>
        </div>
        <div className="w-full rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent mr-3"></div>
            <span className="text-zinc-600 dark:text-zinc-400">Loading stocks...</span>
          </div>
        </div>
      </div>
    );
  }

  if (stocks.length === 0) {
    return (
      <div className="w-full space-y-4">
        <div className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              <span className="font-semibold text-zinc-900 dark:text-white">0</span> stocks in {title}
            </div>
          </div>
        </div>
        <div className="w-full rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 p-8 text-center">
          <div className="text-zinc-400 dark:text-zinc-500 mb-2">
            <EyeIcon className="h-12 w-12 mx-auto" />
          </div>
          <p className="text-zinc-600 dark:text-zinc-400 text-lg">
            {emptyMessage || `No stocks in this ${title} yet`}
          </p>
          <p className="text-zinc-500 dark:text-zinc-500 text-sm mt-2">
            {showRemoveAction ? 'Add stocks to start tracking their performance' : 'Try different filters or check back later'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              <span className="font-semibold text-zinc-900 dark:text-white">{stocks.length}</span> stocks in {title}
            </div>
            {sortConfig.field !== 'name' && (
              <div className="text-sm text-zinc-500 dark:text-zinc-400">
                Sorted by <span className="font-medium">{sortConfig.field.replace(/_/g, ' ')}</span> 
                <span className="ml-1">({sortConfig.direction === 'asc' ? '↑' : '↓'})</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={downloadCSV}
              className="inline-flex items-center px-3 py-2 rounded-md border border-zinc-300 bg-white text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 transition-colors"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-1.5" />
              Export CSV
            </button>
            <div className="hidden text-xs text-zinc-500 md:block dark:text-zinc-400">
              Scroll sideways for more columns
            </div>
          </div>
        </div>
      </div>

      <div className="w-full rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900 overflow-hidden table-container-with-popups">
        <div className="overflow-x-auto max-h-[80vh] scrollbar-thin scrollbar-thumb-zinc-300 dark:scrollbar-thumb-zinc-600 scrollbar-track-zinc-100 dark:scrollbar-track-zinc-900 table-wrapper-popup-safe min-table-height">
          <table className="w-full min-w-[1700px] divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800 sticky top-0 z-[2]">
              <tr>
                {showRemoveAction && (
                  <th className="px-3 py-3 text-left w-20 bg-zinc-50 dark:bg-zinc-800">
                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      Actions
                    </span>
                  </th>
                )}
                {!showRemoveAction && (
                  <th className="px-3 py-3 text-left w-16 bg-zinc-50 dark:bg-zinc-800">
                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
                      View
                    </span>
                  </th>
                )}
                <th className="sticky left-0 z-[3] px-3 py-3 text-left min-w-[180px] sm:min-w-[220px] bg-zinc-50 dark:bg-zinc-800 shadow-[1px_0_0_var(--color-zinc-200)] dark:shadow-[1px_0_0_var(--color-zinc-800)]">
                  <SortButton field="name">Stock Name</SortButton>
                </th>
                {showRemoveAction && (
                  <th className="px-3 py-3 text-right min-w-[95px] bg-zinc-50 dark:bg-zinc-800">
                    <SortButton field="added_at" className="justify-end">Date Added</SortButton>
                  </th>
                )}
                {showRemoveAction && (
                  <th className="px-3 py-3 text-right min-w-[110px] bg-zinc-50 dark:bg-zinc-800">
                    <SortButton field="return_since_add" className="justify-end">Since Added</SortButton>
                  </th>
                )}
                <th className="px-3 py-3 text-right min-w-[110px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="current_price" className="justify-end">Current Price</SortButton>
                </th>
                {showRemoveAction && (
                  <th className="px-3 py-3 text-right min-w-[100px] bg-zinc-50 dark:bg-zinc-800">
                    <SortButton field="price_at_add" className="justify-end">Price at Add</SortButton>
                  </th>
                )}
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_1day" className="justify-end">1 Day</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_1week" className="justify-end">1 Week</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_1month" className="justify-end">1 Month</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_3months" className="justify-end">3 Months</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_6months" className="justify-end">6 Months</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_1year" className="justify-end">1 Year</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_3years" className="justify-end">3 Years</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[75px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="return_over_5years" className="justify-end">5 Years</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[120px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="market_cap" className="justify-end">Market Cap</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[100px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="volume_1day" className="justify-end">Vol (1 Day)</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[100px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="volume_1week" className="justify-end">Vol (1 Week)</SortButton>
                </th>
                <th className="px-3 py-3 text-right min-w-[100px] bg-zinc-50 dark:bg-zinc-800">
                  <SortButton field="volume_1month" className="justify-end">Vol (1 Month)</SortButton>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {sortedStocks.map((stock) => (
                <tr
                  key={stock.isin}
                  className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer table-row-with-menu"
                  onClick={() => openNotesModal(stock)}
                >
                  {showRemoveAction ? (
                    <td className="px-3 py-3 w-20">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenInNewWindow) {
                              onOpenInNewWindow(stock.isin);
                            }
                          }}
                          className="p-2.5 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10 rounded-md transition-colors"
                          title="Open in new window"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </button>
                        {onRemoveStock && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onRemoveStock(stock.isin);
                            }}
                            className="p-2.5 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
                            title="Remove stock"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  ) : (
                    <td className="px-3 py-3 w-16">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onOpenInNewWindow) {
                              onOpenInNewWindow(stock.isin);
                            }
                          }}
                          className="p-2.5 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/10 rounded-md transition-colors"
                          title="Open in new window"
                        >
                          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                  <td className="sticky left-0 z-[1] px-3 py-3 min-w-[180px] max-w-[220px] sm:min-w-[220px] sm:max-w-none bg-white group-hover:bg-zinc-50 dark:bg-zinc-900 dark:group-hover:bg-zinc-800 shadow-[1px_0_0_var(--color-zinc-200)] dark:shadow-[1px_0_0_var(--color-zinc-800)]">
                    <div className="text-left w-full hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
                      <StockActionIcons
                        isin={stock.isin}
                        stockName={stock.name || 'Unknown'}
                        tradingViewLink={stock.trading_view_navlink}
                        screenerLink={stock.screener_navlink}
                        watchlistId={watchlistId}
                        currentPrice={stock.current_price}
                        showNotesCount
                      />
                      <div className="text-sm font-semibold text-zinc-900 dark:text-white truncate">
                        {stock.name || 'Unknown'}
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500 dark:text-zinc-400 mt-0.5">
                        {stock.isin}
                      </div>
                    </div>
                  </td>
                  {showRemoveAction && (
                    <td className="px-3 py-3 text-right min-w-[95px]">
                      <div className="text-sm text-zinc-500 dark:text-zinc-400">
                        {formatAddedDate(stock)}
                      </div>
                    </td>
                  )}
                  {showRemoveAction && (
                    <td className="px-3 py-3 text-right min-w-[110px]">
                      {formatPercentage(stock.return_since_add)}
                    </td>
                  )}
                  <td className="px-3 py-3 text-right min-w-[110px]">
                    <div className="text-sm font-semibold text-zinc-900 dark:text-white">
                      {stock.current_price ? `₹${stock.current_price.toFixed(2)}` : '—'}
                    </div>
                  </td>
                  {showRemoveAction && (
                    <td className="px-3 py-3 text-right min-w-[100px]">
                      <div className="text-sm text-zinc-600 dark:text-zinc-400">
                        {stock.price_at_add ? `₹${stock.price_at_add.toFixed(2)}` : '—'}
                      </div>
                    </td>
                  )}
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_1day)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_1week)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_1month)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_3months)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_6months)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_1year)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_3years)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[75px]">
                    {formatPercentage(stock.return_over_5years)}
                  </td>
                  <td className="px-3 py-3 text-right min-w-[120px]">
                    <div className="text-sm text-zinc-600 dark:text-zinc-400">
                      {formatMarketCap(stock.market_cap)}
                    </div>
                  </td>
                  <td className={`px-3 py-3 text-right min-w-[100px] ${getVolumeHighlighting(stock)}`}>
                    <div className="text-sm rounded-md px-2 py-1">
                      {formatVolume(stock.volume_1day)}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right min-w-[100px]">
                    <div className="text-sm text-zinc-600 dark:text-zinc-400">
                      {formatVolume(stock.volume_1week)}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right min-w-[100px]">
                    <div className="text-sm text-zinc-600 dark:text-zinc-400">
                      {formatVolume(stock.volume_1month)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {isNotesModalOpen && selectedStock && (
        <StockNotesModal
          isOpen={isNotesModalOpen}
          onClose={closeNotesModal}
          isin={selectedStock.isin}
          stockName={selectedStock.name || selectedStock.isin}
          watchlistId={watchlistId}
          currentPrice={selectedStock.current_price}
        />
      )}
    </div>
  );
};

export default WatchlistStocksTable; 